import { getCompanySuppliers } from 'lib/api';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';
import { RootState } from 'services/redux/store';

import SupplierAccounts from './components/SupplierAccounts';

interface SupplierAccount {
  id: number;
  businessIdentifier: string;
  financialStatementsTreeNodesId: number;
  createdAt: string;
  updatedAt: string;
  financialStatementsTreeNodes: {
    id: number;
    key: string;
    accountCode: string;
    accountCodeType: string;
    accountAuxiliaryCode: string;
  };
}

interface Supplier {
  id: number;
  name: string;
  identifier: string;
  businessFinancialStatementNodes?: SupplierAccount[];
}

const headers = [
  { key: 'name', label: 'Proveedor' },
  { key: 'identifier', label: 'Rut' },
];

const NetworkSuppliers: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const lastRefresh = useSelector(
    (state: RootState) => state.businessNetwork.lastRefresh,
  );
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [supplierIdentifier, setSupplierIdentifier] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [accountsDialogOpen, setAccountsDialogOpen] = useState(false);
  const [selectedSupplierIdentifier, setSelectedSupplierIdentifier] =
    useState('');
  const [selectedSupplierName, setSelectedSupplierName] = useState('');
  const [selectedSupplierAccounts, setSelectedSupplierAccounts] = useState<
    SupplierAccount[]
  >([]);
  // Check if user has permission to view network
  const canEditBusinessNamePermission = validatePermissions(
    user,
    'can_edit_business_name',
  );

  const setUp = async (supplierIdentifierSetUp?: string) => {
    setLoading(true);
    try {
      const suppliersResponse = await getCompanySuppliers(
        dispatch,
        supplierIdentifierSetUp
          ? `businessIdentifier=${supplierIdentifierSetUp}`
          : '',
      );
      // La respuesta es un array directo, no un objeto con data y pagination
      const suppliersArray = Array.isArray(suppliersResponse)
        ? suppliersResponse
        : suppliersResponse?.data || [];
      setSuppliers(suppliersArray);
      // Si no hay paginación en la respuesta, crear una por defecto
      if (suppliersResponse?.pagination) {
        setPagination(suppliersResponse.pagination);
      } else {
        setPagination({
          page: 0,
          pageSize: PAGE_SIZE,
          total: suppliersArray.length,
          totalPages: Math.ceil(suppliersArray.length / PAGE_SIZE),
        });
      }
      if (supplierIdentifierSetUp) {
        const supplier = suppliersArray.find(
          (x: Supplier) => x.identifier === supplierIdentifierSetUp,
        );
        if (!supplier?.identifier || !supplier?.name) return;

        dispatch(
          showBusinessNetwork({
            id: supplier.identifier,
            name: supplier.name,
            canEditBusinessName: canEditBusinessNamePermission,
          }),
        );
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  useEffect(() => {
    if (lastRefresh > 0) {
      void setUp();
    }
  }, [lastRefresh]);

  useEffect(() => {
    const handleBusinessNameUpdated = () => {
      void setUp();
    };

    window.addEventListener('businessNameUpdated', handleBusinessNameUpdated);

    return () => {
      window.removeEventListener(
        'businessNameUpdated',
        handleBusinessNameUpdated,
      );
    };
  }, []);

  // Actualizar las cuentas del proveedor seleccionado cuando cambien los proveedores
  useEffect(() => {
    if (accountsDialogOpen && selectedSupplierIdentifier) {
      const updatedSupplier = suppliers.find(
        (s: Supplier) => s.identifier === selectedSupplierIdentifier,
      );
      if (updatedSupplier?.businessFinancialStatementNodes) {
        setSelectedSupplierAccounts(
          updatedSupplier.businessFinancialStatementNodes,
        );
      }
    }
  }, [suppliers, accountsDialogOpen, selectedSupplierIdentifier]);

  const handleDetails = async (id: number) => {
    const supplier = suppliers.find((x: Supplier) => x.id === id);
    if (!supplier?.identifier || !supplier?.name) return;

    setSelectedSupplierIdentifier(supplier.identifier);
    setSelectedSupplierName(supplier.name);
    // Pasar las cuentas del proveedor directamente desde la lista
    setSelectedSupplierAccounts(supplier.businessFinancialStatementNodes || []);
    setAccountsDialogOpen(true);
  };

  const actions = [
    {
      action: handleDetails,
      actionLabel: 'Ver',
    },
  ];

  const generateQuery = ({
    page,
    pageSize,
    supplierIdentifier,
    supplierName,
  }: {
    page: number;
    pageSize: number;
    supplierIdentifier: string;
    supplierName: string;
  }) => {
    let query = '';
    if (supplierIdentifier)
      query += `businessIdentifier=${supplierIdentifier}&`;
    if (supplierName)
      query += `businessName=${supplierName.replace('&', '%26')}&`;
    if (page && pageSize) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const searchNetwork = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({
        page,
        pageSize,
        supplierIdentifier,
        supplierName,
      });
      const response = await getCompanySuppliers(dispatch, query);
      // La respuesta es un array directo, no un objeto con data y pagination
      const suppliersArray = Array.isArray(response)
        ? response
        : response?.data || [];
      setSuppliers(suppliersArray);
      // Si no hay paginación en la respuesta, crear una por defecto
      if (response?.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({
          page,
          pageSize,
          total: suppliersArray.length,
          totalPages: Math.ceil(suppliersArray.length / pageSize),
        });
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const triggerSearch = async () => {
    setLoading(true);
    await searchNetwork({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const filters: Filter[] = [
    {
      label: 'Nombre proveedor',
      type: 'autocomplete',
      value: supplierName,
      key: 'businessName',
      setter: setSupplierName,
    },
    {
      label: 'Rut proveedor',
      type: 'text',
      value: supplierIdentifier,
      setter: setSupplierIdentifier,
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        type="subHeader"
        title="Esta es toda tu red de proveedores."
        variant="body1"
        buttons={[
          {
            name: 'Buscar',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={loading}
              />
            ),
          },
        ]}
      />

      <TableWithPagination
        dataHeaders={headers}
        searchData={searchNetwork}
        data={suppliers}
        setData={setSuppliers}
        showActions
        rowsPerPageDefault={PAGE_SIZE}
        actions={actions}
        pagination={pagination}
        setPagination={setPagination}
        lastElement
      />

      {accountsDialogOpen && (
        <SupplierAccounts
          open={accountsDialogOpen}
          setOpen={setAccountsDialogOpen}
          supplierIdentifier={selectedSupplierIdentifier}
          supplierName={selectedSupplierName}
          initialAccounts={
            suppliers.find(
              (s: Supplier) => s.identifier === selectedSupplierIdentifier,
            )?.businessFinancialStatementNodes || selectedSupplierAccounts
          }
          onRefresh={() => void setUp()}
        />
      )}
    </div>
  );
};

export default NetworkSuppliers;
